/**
 * Theme Configuration
 * 
 * Defines theme interfaces and default theme fallback.
 * Actual themes are loaded from JSON files in fe-wsb/public/themes/
 */

export interface ThemeColors {
  [key: string]: string | undefined
  // Primary colors
  primary: string
  primaryHover: string
  primaryLight: string
  primaryDark: string
  
  // Secondary colors
  secondary: string
  secondaryHover: string
  secondaryLight: string
  secondaryDark: string
  
  // Accent colors
  accent: string
  accentHover: string
  
  // Background colors
  background: string
  backgroundSecondary: string
  backgroundTertiary: string
  surface: string
  surfaceHover: string
  
  // Text colors
  text: string
  textTertiary: string
  textInverse: string
  
  // Border colors
  border: string
  
  // Status colors
  success: string
  successHover: string
  warning: string
  warningHover: string
  error: string
  errorHover: string
  info: string
  infoHover: string
  
  // Interactive colors
  link: string
  linkHover: string
  disabled: string
  disabledText: string
  
  // Special colors (matching fe-wsb theme.css)
  bgPrimary: string
  bgSecondary: string
  bgTertiary: string
  bgCard: string
  bgButton: string
  bgButtonHover: string
  bgButtonDisabled: string
  bgSuccess: string
  bgSuccessHover: string
  bgWarning: string
  bgWarningHover: string
  bgDanger: string
  bgDangerHover: string
  bgDark: string
  bgDarkSecondary: string
  bgDarkTertiary: string
  bgDarkCard: string
  textPrimary: string
  textSecondary: string
  textLight: string
  textMuted: string
  textWhite: string
  textAccent: string
  accentColor: string
  borderColor: string
  borderLight: string
  borderDark: string
  shadowColor: string
  shadowLight: string
  gradientHero: string
  favoriteIconColor?: string
  favoriteIconHoverColor?: string
  favoriteIconActiveColor?: string
  favoriteIconFilter?: string
  favoriteIconHoverFilter?: string
  favoriteIconActiveFilter?: string
}

export interface ThemeTypography {
  fontFamily: string
  fontFamilyMonospace: string
  fontSizeBase: string
  fontSizeSmall: string
  fontSizeLarge: string
  fontSizeH1: string
  fontSizeH4: string
  fontWeightNormal: number
  fontWeightBold: number
  lineHeight: number
}

export interface ThemeSpacing {
  xs: string
  sm: string
  md: string
  lg: string
  xl: string
  xxl: string
}

export interface ThemeAssets {
  // Banner paths
  heroBanner?: string
  heroBannerMobile?: string
  heroBannerSlides?: string[]
  howToPlayBanner?: string
  howToPlayBannerTablet?: string
  
  // Header images
  siteHeaderDesktop?: string
  siteHeaderMobile?: string
  
  // Image paths
  cancelledEvent?: string
  
  // Icon paths
  icons?: {
    [key: string]: string
  }
  
  // Footer images
  footer?: {
    draws?: string
    globalLotteries?: string
    greatMarkets?: string
    fastResults?: string
  }
  
  // How to play images
  howToPlay?: {
    step1?: string
    step2?: string
    step3?: string
    step4?: string
    step5?: string
    step6?: string
  }

  /** Exact game slug → flag filename under /img/flags/ (theme-specific overrides). */
  gameFlagsBySlug?: Record<string, string>

  /** Slug prefix → flag filename (e.g. uk-49s- → uk49s.png). */
  gameFlagsBySlugPrefix?: Record<string, string>
}

export interface ThemeNavigation {
  hideWagersWhenUnauthenticated?: boolean
}

export type ThemeMode = 'light' | 'dark'

export interface ThemeModes {
  light?: ThemeColors
  dark?: ThemeColors
}

export interface Theme {
  name: string
  displayName: string
  colors?: ThemeColors
  modes?: ThemeModes
  typography: ThemeTypography
  spacing: ThemeSpacing
  assets: ThemeAssets
  borderRadius: {
    sm: string
    md: string
    lg: string
    full: string
  }
  shadows: {
    sm: string
    md: string
    lg: string
  }
  navigation?: ThemeNavigation
}

// Default theme matching fe-wsb current theme
export const defaultTheme: Theme = {
  name: 'default',
  displayName: 'Default Theme',
  colors: {
    primary: '#FA6000',
    primaryHover: '#e55a00',
    primaryLight: '#ff7a33',
    primaryDark: '#cc4d00',
    
    secondary: '#1a1f2e',
    secondaryHover: '#2a3447',
    secondaryLight: '#3b4559',
    secondaryDark: '#0f1114',
    
    accent: '#ff6b35',
    accentHover: '#ff5722',
    
    background: '#EEEEEE',
    backgroundSecondary: '#FFFFFF',
    backgroundTertiary: '#f8f9fa',
    surface: '#FFFFFF',
    surfaceHover: '#f5f5f5',
    
    text: '#101520',
    textTertiary: '#999999',
    textInverse: '#ffffff',
    
    border: '#E0E0E0',
    
    success: '#00c853',
    successHover: '#00b34a',
    warning: '#FFC107',
    warningHover: '#ffb300',
    error: '#ff3315',
    errorHover: '#f30000',
    info: '#3498db',
    infoHover: '#2980b9',
    
    link: '#FA6000',
    linkHover: '#e55a00',
    disabled: '#d4d4d4',
    disabledText: '#9d9c9c',
    
    bgPrimary: '#EEEEEE',
    bgSecondary: '#FFFFFF',
    bgTertiary: '#f8f9fa',
    bgCard: '#FFFFFF',
    bgButton: '#ff3315',
    bgButtonHover: '#e55a00',
    bgButtonDisabled: '#1e2732',
    bgSuccess: '#00c853',
    bgSuccessHover: '#00b34a',
    bgWarning: '#FFC107',
    bgWarningHover: '#ffb300',
    bgDanger: '#ff3315',
    bgDangerHover: '#f30000',
    bgDark: '#1a1f2e',
    bgDarkSecondary: '#1c2536',
    bgDarkTertiary: '#1e2732',
    bgDarkCard: '#2B364B',
    textPrimary: '#101520',
    textSecondary: '#666666',
    textLight: '#999999',
    textMuted: '#333333',
    textWhite: '#ffffff',
    textAccent: '#ff6b35',
    accentColor: '#FA6000',
    borderColor: '#E0E0E0',
    borderLight: '#e9ecef',
    borderDark: '#333333',
    shadowColor: 'rgba(0, 0, 0, 0.1)',
    shadowLight: 'rgba(0,0,0,0.05)',
    gradientHero: 'linear-gradient(90deg, #001B24 0%, #003347 100%)',
  },
  typography: {
    fontFamily: 'Ubuntu, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyMonospace: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSizeBase: '0.875rem',
    fontSizeSmall: '0.75rem',
    fontSizeLarge: '1.25rem',
    fontSizeH1: '1.625rem',
    fontSizeH4: '1.25rem',
    fontWeightNormal: 400,
    fontWeightBold: 600,
    lineHeight: 1.4,
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  assets: {
    heroBanner: '/img/images/banners/banner-lg.png',
    heroBannerMobile: '/img/images/banners/banner-sm.png',
    cancelledEvent: '/img/images/cancelled.png',
    footer: {
      draws: '/img/images/footer/01-draws.png',
      globalLotteries: '/img/images/footer/02-global-lotteries.png',
      greatMarkets: '/img/images/footer/03-great-markets.png',
      fastResults: '/img/images/footer/04-fast-results.png',
    },
    howToPlay: {
      step1: '/img/images/how-to-play/1-select-game.png',
      step2: '/img/images/how-to-play/2-select-draws.png',
      step3: '/img/images/how-to-play/3-select-market.png',
      step4: '/img/images/how-to-play/4-confirm-your-entries.png',
      step5: '/img/images/how-to-play/5-your-betslip.png',
      step6: '/img/images/how-to-play/6-wager-details.png',
    },
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    full: '50%',
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 8px rgba(0, 0, 0, 0.1)',
    lg: '0px 0px 15px 5px rgba(163, 163, 163, 0.3)',
  },
}

// Default theme used as fallback when JSON theme not found
export const themes: Record<string, Theme> = {
  default: defaultTheme,
}

/**
 * Get theme by name (fallback only - themes loaded from JSON files)
 */
export function getTheme(name = 'default'): Theme {
  return themes[name] || defaultTheme
}
